import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell, PageHeader } from "../components/AppShell";
import { UnitPills } from "../components/UnitPills";
import { api, formatApiError } from "../lib/api";
import { INGREDIENT_CATEGORIES, catColor, catLabel } from "../lib/constants";
import { Trash2, Save, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
    name: "",
    category: "verdure",
    unit: "kg",
    cost_per_unit: 0,
    waste_pct: 0,
    supplier: "",
    notes: "",
};

export default function IngredientDetail() {
    const { id } = useParams();
    const isNew = id === "new";
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (isNew) return;
        api.get(`/ingredients/${id}`)
            .then(({ data }) => setForm({
                name: data.name || "",
                category: data.category,
                unit: data.unit,
                cost_per_unit: data.cost_per_unit ?? 0,
                waste_pct: data.waste_pct ?? 0,
                supplier: data.supplier || "",
                notes: data.notes || "",
            }))
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setError("");
        if (!form.name.trim()) { setError("Il nome è obbligatorio."); return; }
        if (Number(form.cost_per_unit) < 0) { setError("Il costo non può essere negativo."); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                cost_per_unit: Number(form.cost_per_unit) || 0,
                waste_pct: Number(form.waste_pct) || 0,
            };
            if (isNew) await api.post("/ingredients", payload);
            else await api.put(`/ingredients/${id}`, payload);
            navigate("/ingredients");
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setDeleting(true);
        try {
            await api.delete(`/ingredients/${id}`);
            toast.success("Ingrediente eliminato");
            navigate("/ingredients");
        } catch (e) {
            setError(formatApiError(e));
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
            setDeleteOpen(false);
        }
    };

    if (loading) {
        return (
            <AppShell>
                <div className="py-16 text-center text-sm text-brand-muted">Caricamento…</div>
            </AppShell>
        );
    }

    return (
        <AppShell>
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink transition"
                data-testid="back-btn"
            >
                <ArrowLeft size={14} /> Indietro
            </button>

            <div className="mt-3 flex items-end justify-between gap-6 flex-wrap mb-8">
                <div>
                    <div className="eyebrow text-brand-gold mb-2">
                        {isNew ? "Nuovo ingrediente" : "Modifica ingrediente"}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-brand-ink leading-tight">
                        {form.name || "Senza nome"}
                    </h1>
                </div>
                {!isNew && (
                    <button
                        onClick={() => setDeleteOpen(true)}
                        data-testid="ingredient-delete"
                        className="inline-flex items-center gap-2 text-sm text-brand-danger border border-brand-danger/30 hover:bg-brand-danger/10 rounded-full px-4 py-2 transition"
                    >
                        <Trash2 size={14} /> Elimina
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-5 rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5" data-testid="ingredient-error">
                    {error}
                </div>
            )}

            <div className="bg-paper rounded-2xl border border-brand-line shadow-card p-6 sm:p-8 space-y-6">
                <div>
                    <Label>Nome</Label>
                    <input
                        data-testid="ing-name"
                        value={form.name}
                        onChange={(e) => set("name")(e.target.value)}
                        placeholder="Es. Manzo filetto"
                        className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                    />
                </div>

                <div>
                    <Label>Categoria</Label>
                    <div className="flex flex-wrap gap-2" data-testid="ing-categories">
                        {INGREDIENT_CATEGORIES.map((c) => {
                            const color = catColor(c);
                            const active = form.category === c;
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => set("category")(c)}
                                    data-testid={`ing-cat-${c}`}
                                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border transition ${
                                        active ? "text-white" : "bg-white text-brand-ink/70 border-brand-line hover:border-brand-gold/50"
                                    }`}
                                    style={active ? { background: color, borderColor: color } : undefined}
                                >
                                    {catLabel(c)}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <Label>Unità di misura</Label>
                    <UnitPills value={form.unit} onChange={set("unit")} testid="ing-unit" />
                    {form.unit === "pz" && form.category !== "bevande" && (
                        <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-[12px] px-3 py-2 flex items-start gap-2">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>
                                L'unità <strong>pezzo (pz)</strong> falsa il food cost
                                se l'ingrediente ha peso variabile (uova, limoni, avocado…).
                                Usa <strong>g / kg</strong> con il peso medio realistico.
                                Solo le bevande confezionate possono restare in pz.
                            </span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                        <Label>Costo per unità (€)</Label>
                        <input
                            data-testid="ing-cost"
                            type="number"
                            step="0.001"
                            min="0"
                            value={form.cost_per_unit}
                            onChange={(e) => set("cost_per_unit")(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                        />
                    </div>
                    <div>
                        <Label>Scarto (%)</Label>
                        <input
                            data-testid="ing-waste"
                            type="number"
                            step="1"
                            min="0"
                            max="99"
                            value={form.waste_pct}
                            onChange={(e) => set("waste_pct")(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                        />
                        <div className="text-[11px] text-brand-muted mt-1.5">
                            Es. pesce da pulire 40%, verdure 10-20%.
                        </div>
                    </div>
                </div>

                <div>
                    <Label>Fornitore</Label>
                    <input
                        data-testid="ing-supplier"
                        value={form.supplier}
                        onChange={(e) => set("supplier")(e.target.value)}
                        placeholder="Es. Mercato generale"
                        className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                    />
                </div>

                <div>
                    <Label>Note</Label>
                    <textarea
                        data-testid="ing-notes"
                        value={form.notes}
                        onChange={(e) => set("notes")(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold resize-none"
                    />
                </div>
            </div>

            <div className="sticky bottom-20 lg:bottom-4 mt-6 flex justify-end">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="ingredient-save"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-7 py-3 text-sm font-semibold hover:bg-brand-navyLight transition disabled:opacity-60 shadow-card"
                >
                    <Save size={16} />
                    {saving ? "Salvataggio…" : "Salva"}
                </button>
            </div>

            {deleteOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
                    onClick={() => !deleting && setDeleteOpen(false)}
                    data-testid="ingredient-delete-dialog"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden"
                    >
                        <div className="px-6 py-5 border-b border-brand-line">
                            <div className="h-11 w-11 rounded-full bg-red-50 text-brand-danger flex items-center justify-center mb-3">
                                <Trash2 size={20} />
                            </div>
                            <h3 className="font-display text-2xl text-brand-ink">
                                Eliminare questo ingrediente?
                            </h3>
                            <p className="mt-2 text-sm text-brand-muted">
                                <strong>{form.name || "Senza nome"}</strong> verrà rimosso
                                definitivamente. Le ricette che lo contengono resteranno ma
                                con la riga riferita come "ingrediente mancante".
                            </p>
                        </div>
                        <div className="px-6 py-4 bg-brand-cream flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setDeleteOpen(false)}
                                disabled={deleting}
                                data-testid="ingredient-delete-cancel"
                                className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={remove}
                                disabled={deleting}
                                data-testid="ingredient-delete-confirm"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-danger text-white text-sm font-bold hover:opacity-90 disabled:opacity-60 transition"
                            >
                                <Trash2 size={14} /> {deleting ? "Elimino…" : "Sì, elimina"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}

function Label({ children }) {
    return <div className="eyebrow text-brand-ink/60 mb-1.5">{children}</div>;
}
